import { useState, useCallback } from 'react';
import { EnvelopeSimple, X, ArrowClockwise } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Non-blocking banner shown to unverified email/password users during the
 * 3-day grace period. Dismissible per session — reappears on next app open.
 */
export default function EmailVerificationBanner() {
  const { resendVerificationEmail } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleResend = useCallback(async () => {
    if (resending || resent) return;
    setResending(true);
    try {
      await resendVerificationEmail();
      setResent(true);
    } catch {
      // Silently fail — user can retry
    } finally {
      setResending(false);
    }
  }, [resending, resent, resendVerificationEmail]);

  if (dismissed) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 px-4 py-2.5 flex items-center justify-between gap-3 safe-top">
      <div className="flex items-center gap-2 min-w-0">
        <EnvelopeSimple size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
        <p className="text-xs text-amber-800 dark:text-amber-200 truncate">
          {resent ? 'Verification email sent! Check your inbox.' : 'Verify your email to keep your account'}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {!resent && (
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-xs font-medium text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 flex items-center gap-1 disabled:opacity-50"
          >
            <ArrowClockwise size={14} className={resending ? 'animate-spin' : ''} />
            Resend
          </button>
        )}
        <button
          onClick={() => setDismissed(true)}
          className="p-0.5 text-amber-500 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-200"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
